import { Component } from '@theme/component';

class IlmBookRail extends Component {
  #events;
  #resize;
  #frame;

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#setup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#clear();
  }

  #clear() {
    this.#events?.abort();
    this.#resize?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  #setup() {
    this.#clear();
    const track = this.querySelector('[data-ilm-rail-track]');
    const controls = this.querySelector('[data-ilm-rail-controls]');
    if (!(track instanceof HTMLElement) || !(controls instanceof HTMLElement)) return;
    this.#events = new AbortController();
    const { signal } = this.#events;
    const previous = controls.querySelector('[data-ilm-rail-prev]');
    const next = controls.querySelector('[data-ilm-rail-next]');
    const update = () => {
      const overflow = track.scrollWidth - track.clientWidth;
      controls.hidden = overflow <= 2;
      const position = Math.abs(track.scrollLeft);
      if (previous instanceof HTMLButtonElement) previous.disabled = position <= 2;
      if (next instanceof HTMLButtonElement) next.disabled = position >= overflow - 2;
    };
    const scroll = (direction) => {
      const rtl = getComputedStyle(track).direction === 'rtl';
      const child = track.firstElementChild;
      const gap = Number.parseFloat(getComputedStyle(track).columnGap) || 0;
      const width = child?.getBoundingClientRect().width || track.clientWidth;
      const step = (width + gap) * Math.max(1, Math.floor(track.clientWidth / (width + gap)));
      track.scrollBy({
        left: step * direction * (rtl ? -1 : 1),
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
    };
    previous?.addEventListener('click', () => scroll(-1), { signal });
    next?.addEventListener('click', () => scroll(1), { signal });
    track.addEventListener('scroll', () => {
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(update);
    }, { signal, passive: true });
    this.#resize = new ResizeObserver(update);
    this.#resize.observe(track);
    update();
  }
}

class IlmDirectory extends Component {
  #events;

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#setup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#events?.abort();
  }

  #setup() {
    this.#events?.abort();
    const input = this.querySelector('[data-ilm-directory-search]');
    const search = this.querySelector('[data-ilm-directory-search-wrap]');
    const status = this.querySelector('[data-ilm-directory-status]');
    if (!(input instanceof HTMLInputElement)) return;
    this.#events = new AbortController();
    if (search instanceof HTMLElement) search.hidden = false;
    const filter = () => {
      const query = input.value.trim().toLocaleLowerCase();
      const names = new Set();
      for (const group of this.querySelectorAll('.collection-directory__group')) {
        let visible = 0;
        for (const row of group.querySelectorAll('li')) {
          const name = row.querySelector('a span')?.textContent || '';
          row.hidden = !name.toLocaleLowerCase().includes(query);
          if (!row.hidden) {
            visible++;
            names.add(name.trim().toLocaleLowerCase());
          }
        }
        group.hidden = visible === 0;
      }
      const featured = this.querySelector('.collection-directory__featured');
      if (featured) {
        let visible = 0;
        for (const row of featured.querySelectorAll('li')) {
          const name = row.querySelector('.collection-directory__feature-name')?.textContent || '';
          row.hidden = !name.toLocaleLowerCase().includes(query);
          if (!row.hidden) {
            visible++;
            names.add(name.trim().toLocaleLowerCase());
          }
        }
        featured.hidden = visible === 0;
      }
      const alphabet = this.querySelector('.collection-directory__az');
      if (alphabet) alphabet.hidden = Boolean(query);
      if (status) status.textContent = query ? (this.dataset.labelResults || '[count]').replace('[count]', String(names.size)) : '';
    };
    input.addEventListener('input', filter, { signal: this.#events.signal });
    filter();
  }
}

if (!customElements.get('ilm-book-rail')) customElements.define('ilm-book-rail', IlmBookRail);
if (!customElements.get('ilm-directory')) customElements.define('ilm-directory', IlmDirectory);
